import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';

import { Subject, fromEvent, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MwSidebarService } from './sidebar.service';

@Component({
  selector: 'mw-sidebar',
  templateUrl: 'sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MwSidebarComponent implements OnInit, OnDestroy {
  @Output() foldedChange = new EventEmitter<boolean>();
  @Output() openChange = new EventEmitter<boolean>();

  @Input() name: string;
  @Input() invisibleOverlay = false;
  @Input() position: 'left' | 'right' = 'left';
  @Input() lockedOpen: boolean;
  @Input() foldedWidth = 64;
  @Input() width = 264;
  @Input() hostElement;

  @Input()
  set folded(value: boolean) {
    this.localFolded = value;

    if (value === this.localFolded || !this.opened) {
      return;
    }

    if (this.localFolded) {
      this.fold();
    } else {
      this.unfold();
    }
  }

  get folded(): boolean {
    return this.localFolded;
  }

  private localFolded = false;
  private hostEl: ElementRef;
  private unsubscribeAll$ = new Subject<void>();

  constructor(
    private sidebarService: MwSidebarService,
    private changeDetectorRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {}

  @HostBinding('class.mw-sidebar--open')
  opened = false;

  @HostBinding('class.mw-sidebar--locked-open')
  isLockedOpen: boolean;

  ngOnInit(): void {
    this.sidebarService.register(this.name, this);
    this.setupPosition();
    this.setupLockedOpen();
    this.setupFolded();

    if (document) {
      merge(
        fromEvent(document, 'touchstart', { capture: true }),
        fromEvent(document, 'mousedown', { capture: true })
      )
        .pipe(takeUntil(this.unsubscribeAll$))
        .subscribe((event) => this.onOutsideClick(event));
    }

    this.sidebarService.sidebarStateChange.next(this);
  }

  ngOnDestroy(): void {
    this.sidebarService.unregister(this.name);
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  onOutsideClick(event: any): void {
    const isSidebarClicked = this.elementRef.nativeElement.contains(
      event.target
    );
    const isHostClicked =
      this.hostEl && this.hostEl.nativeElement.contains(event.target);

    if (
      !this.opened ||
      this.isLockedOpen ||
      isSidebarClicked ||
      isHostClicked
    ) {
      return;
    }

    const path = event.path || (event.composedPath && event.composedPath());
    if (
      event.target &&
      event.target.shadowRoot &&
      path &&
      path[0] &&
      this.elementRef.nativeElement.contains(path[0])
    ) {
      return;
    }

    this.close();
  }

  toggleOpen(): void {
    if (this.opened) {
      this.close();
    } else {
      this.open();
    }
  }

  open(hostEl?: ElementRef, closeIfOpened: boolean = false): void {
    if (this.opened) {
      if (closeIfOpened) {
        this.close();
      }
      return;
    }

    this.opened = true;
    this.openChange.emit(this.opened);
    this.changeDetectorRef.markForCheck();
    this.hostEl = hostEl;
  }

  close(): void {
    if (!this.opened) {
      return;
    }

    this.opened = false;
    this.openChange.emit(this.opened);
    this.changeDetectorRef.markForCheck();
  }

  fold({
    omitEvent,
    force,
  }: { omitEvent?: boolean; force?: boolean } = {}): void {
    if (this.localFolded && !force) {
      return;
    }

    this.localFolded = true;

    const styleValue = this.foldedWidth + 'px';

    ['width', 'min-width', 'max-width'].forEach((style) =>
      this.renderer.setStyle(this.elementRef.nativeElement, style, styleValue)
    );

    this.addClass('mw-sidebar--folded');

    if (!omitEvent) {
      this.foldedChange.emit(this.folded);
      this.sidebarService.sidebarStateChange.next(this);
    }

    this.changeDetectorRef.markForCheck();
  }

  unfold({
    omitEvent,
    force,
  }: { omitEvent?: boolean; force?: boolean } = {}): void {
    if (!this.folded && !force) {
      return;
    }

    this.localFolded = false;

    this.setDefaultWidth();

    this.removeClass('mw-sidebar--folded');

    if (!omitEvent) {
      this.foldedChange.emit(this.localFolded);
      this.sidebarService.sidebarStateChange.next(this);
    }

    this.changeDetectorRef.markForCheck();
  }

  toggleFold(): void {
    if (this.localFolded) {
      this.unfold();
    } else {
      this.fold();
    }
  }

  private setupPosition(): void {
    this.addClass(`mw-sidebar--${this.position}-positioned`);
  }

  private setupLockedOpen(): void {
    if (!this.lockedOpen) {
      return;
    }

    this.opened = true;
    this.isLockedOpen = true;

    this.changeDetectorRef.markForCheck();
  }

  private setupFolded(): void {
    if (!this.opened) {
      this.setDefaultWidth();
      return;
    }

    if (this.localFolded) {
      this.fold({ omitEvent: true, force: true });
    } else {
      this.unfold({ omitEvent: true, force: true });
    }
  }

  private addClass(className: string): void {
    this.renderer.addClass(this.elementRef.nativeElement, className);
  }

  private removeClass(className: string): void {
    this.renderer.removeClass(this.elementRef.nativeElement, className);
  }

  private setDefaultWidth(): void {
    const styleValue = this.width + 'px';
    ['width', 'min-width', 'max-width'].forEach((style) =>
      this.renderer.setStyle(this.elementRef.nativeElement, style, styleValue)
    );
  }
}
